"use strict";
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.AssetManager = void 0;
const utils_1 = require("../utils");
class AssetManager {
    constructor(httpAdapter, adapterFactory, socketAdapter, accessTokenProvider, storageAdapter) {
        this.assetErrorDictionary = new Map();
        // Concurrent callers for the same URL attach to the same in-flight promise.
        this.inFlight = new Map();
        this.httpAdapter = httpAdapter;
        this.socketAdapter = socketAdapter;
        this.assetDictionary = new Map();
        this.adapterFactory = adapterFactory;
        this.accessTokenProvider = accessTokenProvider;
        this.storageAdapter = storageAdapter;
    }
    hasRecentError(url) {
        const ts = this.assetErrorDictionary.get(url);
        if (ts === undefined)
            return false;
        if ((0, utils_1.nowInMilliseconds)() - ts > AssetManager.ERROR_CACHE_MS) {
            this.assetErrorDictionary.delete(url);
            return false;
        }
        return true;
    }
    getSignedUrl(url) {
        return __awaiter(this, void 0, void 0, function* () {
            const body = {
                url,
            };
            return yield this.httpAdapter.getCommonAdapter().getSignedUrl(body);
        });
    }
    getSignedUrlInfo(url_1) {
        return __awaiter(this, arguments, void 0, function* (url, fetchFresh = false) {
            if (fetchFresh) {
                this.assetErrorDictionary.delete(url);
            }
            if (!fetchFresh && this.assetDictionary.has(url)) {
                const existingUrl = this.assetDictionary.get(url);
                if ((0, utils_1.convertToMilliseconds)(existingUrl.expiry) > (0, utils_1.nowInMilliseconds)()) {
                    return existingUrl;
                }
            }
            if (!fetchFresh && this.hasRecentError(url)) {
                return undefined;
            }
            const existingPromise = this.inFlight.get(url);
            if (existingPromise) {
                return existingPromise;
            }
            const promise = (() => __awaiter(this, void 0, void 0, function* () {
                const signedData = yield this.getSignedUrl(url);
                if (!signedData) {
                    // Surface to caller and remember the failure so we don't hammer the
                    // backend for the next ERROR_CACHE_MS.
                    console.error("AssetManager: malformed getSignedUrl response", { url });
                    this.assetErrorDictionary.set(url, (0, utils_1.nowInMilliseconds)());
                    return undefined;
                }
                // Backend's `expiry` is "ms remaining from now", not a date — convert
                // to absolute so the cache freshness check compares like-to-like.
                const stored = Object.assign(Object.assign({}, signedData), { expiry: (0, utils_1.nowInMilliseconds)() + signedData.expiry });
                this.assetDictionary.set(url, stored);
                this.assetErrorDictionary.delete(url);
                return stored;
            }))();
            this.inFlight.set(url, promise);
            try {
                return yield promise;
            }
            finally {
                this.inFlight.delete(url);
            }
        });
    }
    getSignedUrlInfoWithSocket(url, callback, fetchFresh = false) {
        // A fresh user-initiated fetch must forget any cached failure, otherwise
        // the negative cache below would silently swallow the retry.
        if (fetchFresh) {
            this.assetErrorDictionary.delete(url);
        }
        if (!fetchFresh && this.assetDictionary.has(url)) {
            const signedData = this.assetDictionary.get(url);
            // Without the buffer time, signed url could expire before any UI component involving async code uses the signed url.
            // e.g. useEffect code setting image url that gets loaded on a img tag
            const bufferTimeBeforeExpiry = 5000;
            if (signedData &&
                (0, utils_1.convertToMilliseconds)(signedData.expiry) >
                    (0, utils_1.nowInMilliseconds)() + bufferTimeBeforeExpiry) {
                callback(signedData);
                return;
            }
        }
        if (!fetchFresh && this.hasRecentError(url)) {
            callback(undefined);
            return;
        }
        const existingPromise = this.inFlight.get(url);
        if (existingPromise) {
            void (() => __awaiter(this, void 0, void 0, function* () {
                try {
                    callback(yield existingPromise);
                }
                catch (_a) {
                    callback(undefined);
                }
            }))();
            return;
        }
        const promise = new Promise(resolve => {
            const responseCallback = (data) => {
                const signedData = data === null || data === void 0 ? void 0 : data.data;
                if (!signedData) {
                    console.error("AssetManager: malformed getMediaSignedUrl response", {
                        url,
                        statusCode: data === null || data === void 0 ? void 0 : data.statusCode,
                        hasData: !!(data === null || data === void 0 ? void 0 : data.data),
                    });
                    this.assetErrorDictionary.set(url, (0, utils_1.nowInMilliseconds)());
                    resolve(undefined);
                    return;
                }
                // Backend's `expiry` is "ms remaining from now", not a date — convert
                // to absolute so the cache freshness check compares like-to-like.
                const stored = Object.assign(Object.assign({}, signedData), { expiry: (0, utils_1.nowInMilliseconds)() + signedData.expiry });
                this.assetDictionary.set(url, stored);
                this.assetErrorDictionary.delete(url);
                resolve(stored);
            };
            this.socketAdapter.getMediaSignedUrl(url, responseCallback);
        });
        this.inFlight.set(url, promise);
        void (() => __awaiter(this, void 0, void 0, function* () {
            try {
                callback(yield promise);
            }
            catch (_a) {
                callback(undefined);
            }
            finally {
                this.inFlight.delete(url);
            }
        }))();
    }
    uploadToSignedUrl(file, key, uploadHandler, onUploadProgress, onUploadSuccess, onUploadError, isThumbnail) {
        return __awaiter(this, void 0, void 0, function* () {
            return yield this.storageAdapter.uploadToSignedUrl(file, key, uploadHandler, onUploadProgress, onUploadSuccess, onUploadError, isThumbnail);
        });
    }
}
exports.AssetManager = AssetManager;
AssetManager.ERROR_CACHE_MS = 60000;
